/**
 * Auth Slice
 * Redux slice for authentication state management
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { User, UserContext, ContextData } from '@/types/shared/auth.types'

interface AuthState {
  user: User | null
  contexts: UserContext[]
  currentContext: UserContext | null
  contextData: ContextData | null // Full context data from cache (branch_ids, roles, permissions)
  /** Branch/org ERP workspace shell; persisted per org+branch. */
  activeWorkspace: string | null
  contextLifecycle: 'STABLE' | 'REFRESHING_POLICY' | 'INVALID'
  accessToken: string | null
  refreshToken: string | null
  contextToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  contexts: [],
  currentContext: null,
  contextData: null, // Full context data from cache
  activeWorkspace: null,
  contextLifecycle: 'REFRESHING_POLICY',
  accessToken: null,
  refreshToken: null,
  contextToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User
        accessToken: string
        refreshToken: string
        contextToken?: string
        contexts?: UserContext[]
      }>
    ) => {
      state.user = action.payload.user
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
      state.contextToken = action.payload.contextToken || null
      state.contexts = action.payload.contexts || []
      state.isAuthenticated = true
      state.error = null
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
    },
    setContexts: (state, action: PayloadAction<UserContext[]>) => {
      state.contexts = action.payload
    },
    setCurrentContext: (state, action: PayloadAction<UserContext>) => {
      state.currentContext = action.payload
    },
    setContextToken: (state, action: PayloadAction<string>) => {
      state.contextToken = action.payload
    },
    setContextData: (state, action: PayloadAction<ContextData | null>) => {
      state.contextData = action.payload
    },
    setActiveWorkspace: (state, action: PayloadAction<string | null>) => {
      state.activeWorkspace = action.payload
    },
    setContextRefreshing: (state) => {
      state.contextLifecycle = 'REFRESHING_POLICY'
    },
    setContextStable: (state) => {
      state.contextLifecycle = 'STABLE'
    },
    setContextInvalid: (state) => {
      state.contextLifecycle = 'INVALID'
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    resetAuthState: () => initialState,
    logout: (state) => {
      state.user = null
      state.contexts = []
      state.currentContext = null
      state.contextData = null
      state.activeWorkspace = null
      state.contextLifecycle = 'REFRESHING_POLICY'
      state.accessToken = null
      state.refreshToken = null
      state.contextToken = null
      state.isAuthenticated = false
      state.error = null
    },
    /** Clear context + policy so layout falls into policyLoading/redirect. Use on bootstrap failure, 401/403, context switch rollback. */
    resetContextData: (state) => {
      state.contextData = null
      state.activeWorkspace = null
      state.contextLifecycle = 'REFRESHING_POLICY'
    },
    /** Full workspace session wipe (tokens cleared separately). Replace state—no merge—for clean login/context switch. */
    workspaceSessionReset: (state) => {
      state.contextToken = null
      state.contextData = null
      state.currentContext = null
      state.contexts = []
      state.activeWorkspace = null
      state.contextLifecycle = 'REFRESHING_POLICY'
      state.user = null
    },
  },
})

export const {
  setCredentials,
  setUser,
  setContexts,
  setCurrentContext,
  setContextToken,
  setContextData,
  setActiveWorkspace,
  setContextRefreshing,
  setContextStable,
  setContextInvalid,
  setLoading,
  setError,
  resetAuthState,
  logout,
  resetContextData,
  workspaceSessionReset,
} = authSlice.actions

export default authSlice.reducer

